import { Injectable, inject } from '@angular/core';
import type { IProduct } from '@becksdevteam/becks-invoicing';
import type { Observable } from 'rxjs';
import type { PaginationResult } from '../../core/models/paginated-result.type';
import type { IFamily } from '../../library/becks-family';

import { buildQueryParams } from '../helpers/query-params.helper';
import type { FamilyProductConfig } from '../models/family-product-config.interface';
import type {
  CompanyTechType,
  CompanyTechTypesFilter,
  GradeSize,
  PackageType,
  PriceZoneSettingsApi,
  Product,
  ProductFilters,
  ProductPrice,
  TechType,
  TechTypeCompany,
  TechTypesFilter,
  Trait,
  TreatmentType,
} from '../models/product.interface';
import type { BatchSummary, BatchUpdateBody } from '../models/product-batch-update.interface';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private _apiService = inject(ApiService);

  public fetchParentProductIDs(familyID: number): Observable<string[]> {
    return this._apiService.get(`/products/parentsByFamily?familyID=${familyID}`);
  }

  public fetchProductIDs(parentID: number): Observable<string[]> {
    return this._apiService.get(`/products/productsByParent?parentID=${parentID}`);
  }

  public fetchProductDetail(parentID: number | string): Observable<IProduct> {
    return this._apiService.get(`/products/${parentID}/productDetail`);
  }

  public fetchFamilies(): Observable<IFamily[]> {
    return this._apiService.get<IFamily[]>('/products/families');
  }

  public fetchTraits(): Observable<Trait[]> {
    return this._apiService.get<Trait[]>('/v2/product-traits');
  }

  public fetchTreatments(): Observable<TreatmentType[]> {
    return this._apiService.get<TreatmentType[]>('/v2/product-treatments');
  }

  public fetchPackageTypes(): Observable<PackageType[]> {
    return this._apiService.get<PackageType[]>('/v2/product-package-types');
  }

  public fetchGradeSizes(): Observable<GradeSize[]> {
    return this._apiService.get<GradeSize[]>('/v2/product-grade-sizes');
  }

  public fetchPriceZoneSettings(salesYear: string): Observable<PriceZoneSettingsApi> {
    const queryString = buildQueryParams({ salesYear });
    return this._apiService.get<PriceZoneSettingsApi>(`/v2/product-price-zone-settings?${queryString}`);
  }

  public fetchProducts(filters: ProductFilters, salesYear: string): Observable<PaginationResult<Product>> {
    const queryString = buildQueryParams({ salesYear, ...filters });
    return this._apiService.get<PaginationResult<Product>>(`/v2/products?${queryString}`);
  }

  public fetchProductPrices(filters: ProductFilters, salesYear: string): Observable<PaginationResult<ProductPrice>> {
    const queryString = buildQueryParams({ salesYear, ...filters });
    return this._apiService.get<PaginationResult<ProductPrice>>(`/v2/product-prices?${queryString}`);
  }

  public batchUpdate(body: BatchUpdateBody, salesYear: string): Observable<BatchSummary> {
    const queryString = buildQueryParams({ salesYear });
    return this._apiService.post<BatchSummary>(`/v2/product-batch-updates?${queryString}`, body);
  }

  public fetchFamilyConfigs(): Observable<FamilyProductConfig[]> {
    return this._apiService.get<FamilyProductConfig[]>('/v2/product-family-configs');
  }

  public fetchTechTypeCompanies(): Observable<TechTypeCompany[]> {
    return this._apiService.get<TechTypeCompany[]>('/v2/product-tech-type-companies');
  }

  public fetchTechTypes(filter?: TechTypesFilter): Observable<TechType[]> {
    const queryString = buildQueryParams({ ...filter });
    return this._apiService.get<TechType[]>(`/v2/product-tech-types?${queryString}`);
  }

  public fetchCompanyTechTypes(filter: CompanyTechTypesFilter): Observable<CompanyTechType[]> {
    const queryString = buildQueryParams({ ...filter });
    return this._apiService.get<CompanyTechType[]>(`/v2/product-tech-types?${queryString}`);
  }
}
